import {
  ChangeEvent,
  MutableRefObject,
  useCallback,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';

import {
  calcCursorPosition,
  getCursorPosition,
  moveCursorPosition,
} from '../helpers/cursorPosition';
import { Formatter, Parser } from '../helpers/formatter';

const SELECTABLE_TYPES = /text|password|search|tel|url/;

type Params = {
  ref: MutableRefObject<HTMLInputElement | null>;
  externalValue?: string;
  parser?: Parser;
  onChange?: (value: string) => void;
  formatter?: Formatter;
  isFocused: boolean;
};

export const useInput = ({
  isFocused,
  ref,
  onChange,
  externalValue = '',
  parser,
  formatter,
}: Params): [string, (event: ChangeEvent<HTMLInputElement>) => void] => {
  const [inputValue, setInputValue] = useState('');
  const prevValueRef = useRef<string>('');

  const parseValue = (value: string) => {
    if (parser) {
      return parser(value);
    }

    return value;
  };

  const formatValue = useCallback(
    (value: string, cursor: number) => {
      if (formatter) {
        const result = formatter(value, prevValueRef.current, cursor, { isFocused });
        if (typeof result === 'string') {
          return { value: result, cursor };
        }

        return { value: result.value, cursor: result.cursor };
      }

      return { value, cursor };
    },
    [formatter, isFocused]
  );

  useLayoutEffect(() => {
    const formattedValue = formatValue(externalValue, 0).value;
    setInputValue(formattedValue);
  }, [externalValue, formatValue]);

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const {
      target: { value, type },
    } = event;
    const parsedValue = parseValue(value);

    const isSelectionSupported = SELECTABLE_TYPES.test(type);

    const cursorValue = isSelectionSupported ? getCursorPosition(ref) : 0;
    const formattedResult = formatValue(parsedValue, cursorValue);
    const { value: formattedValue } = formattedResult;

    if (ref.current && formattedValue !== value) {
      // eslint-disable-next-line no-param-reassign
      ref.current.value = formattedValue;
    }

    if (isSelectionSupported) {
      const newCursor = calcCursorPosition(value, prevValueRef.current, formattedResult);

      if (formattedValue !== value || cursorValue !== newCursor) {
        moveCursorPosition(ref, newCursor);
      }
    }

    prevValueRef.current = formattedValue;
    setInputValue(formattedValue);

    if (onChange) {
      onChange(parsedValue);
    }
  };

  return [inputValue, handleChange];
};

export default useInput;
